import { MeshBasicMaterial, Mesh, PlaneGeometry } from "three";
import { VBase } from "./VBase";
import VUtil from "../VUtils";

const defaultOptions = {
  map: null,
  color: 0x676767,
  opacity: 1,
  handleOpacity: 1,
  handleColor: 0x00ffff,
  handleMap: null,
  width: 1.8,
  height: 0.05,
  radius: 0.02,
  onValueChange: null,
  onValueChangeStart: null,
  onValueChangeEnd: null,
  disabledColor: 0xc8c8c8,
  enabledCb: null,
};

/*
  使用案例
    const sliderBgPath = "/assets/vrUI/skyPanel/sliderBg1.png";
      const sliderBg = await this.manager.loadManager.loadTexture(sliderBgPath);

      const slider = this.uiLib.createSlider2({
        map: sliderBg,
        color: 0xffffff,
        opacity: 0.8,
        handleColor: 0x00ff00,
        fillOpacity: 0.5,
        width: 0.75,
        height: 0.03,
        radius: 0.015,
      });
      // 设置进度条当前数值
      slider.setProgressValue(0.5);
      // 注册进度值改变事件 可选
      slider.addOnValueChangeEvent((v) => {
        console.log("进度值在改变中：" + v);
      });
      // 注册进度值改变开始事件 可选
      slider.addOnValueChangeStartEvent((v) => {
        console.log("进度值改变开始：" + v);
      });
      // 注册进度值改变结束事件 可选
      slider.addOnValueChangeEndEvent((v) => {
        console.log("进度值改变结束：" + v);
      });
      // 注册光标进入handle事件 可选
      slider.addHandlerHoverEnterEvent((handle) => {
        console.log("光标进入handle");
      });
      // 注册光标离开handle事件 可选
      slider.addHandlerHoverExitEvent((handle) => {
        console.log("光标离开handle");
      });

      this.uiLib.addUIToScene(slider);

 */

export class VSlider2 extends VBase {
  /**
   * 没有填充的滑动条，通常用于滚动区域
   *   options:
   * {
   *      priority: 优先级，默认为1，值越小越靠前
   *      renderOrder: 渲染顺序，默认为99998
   *      color: 进度条背景色，默认为0x676767
   *      map: 进度条背景贴图，默认为null，不是地址
   *      handleMap: 进度条handle贴图，默认为null，不是地址
   *      handleColor: handle颜色，默认为0x00ffff
   *      opacity: 进度条背景透明度，默认为1
   *      handleOpacity: handle透明度，默认为1
   *      width: 进度条宽度，默认为1.8
   *      height: 进度条高度，默认为0.05
   *      radius: 圆角矩形半径，默认为0.02
   *      disabledColor:  ui对象禁用颜色，默认为 0xC8C8C8
   * }
   * @param {*} options
   */
  constructor(options, vuiLib) {
    if (!options) options = {};

    const tempOptions = {
      priority: options.priority !== undefined ? options.priority : 1,
      renderOrder:
        options.renderOrder !== undefined ? options.renderOrder : 99998,
      color: options.color !== undefined ? options.color : defaultOptions.color,
      map: options.map !== undefined ? options.map : defaultOptions.map,
      opacity:
        options.opacity !== undefined
          ? options.opacity
          : defaultOptions.opacity,
      handleOpacity:
        options.handleOpacity !== undefined
          ? options.handleOpacity
          : defaultOptions.handleOpacity,
      handleColor:
        options.handleColor !== undefined
          ? options.handleColor
          : defaultOptions.handleColor,
      handleMap:
        options.handleMap !== undefined
          ? options.handleMap
          : defaultOptions.handleMap,
      width: options.width !== undefined ? options.width : defaultOptions.width,
      height:
        options.height !== undefined ? options.height : defaultOptions.height,
      radius:
        options.radius !== undefined ? options.radius : defaultOptions.radius,
      disabledColor:
        options.disabledColor !== undefined
          ? options.disabledColor
          : defaultOptions.disabledColor,
    };

    super(tempOptions, vuiLib);
    this._vType = "VSlider2";
    this._otherProps = {
      handleOpacity: tempOptions.handleOpacity,
      handleColor: tempOptions.handleColor,
      handleMap: tempOptions.handleMap,
      onValueChangeStart: [],
      onValueChange: [],
      onValueChangeEnd: [],
      handleHoverEnter: [],
      handleHoverExit: [],
    };

    this.lastTarget = 0;
    this.value = 0; // 当前滑块所在位置，0到1的小数

    this.name = "VSlider2";

    // handle
    this.handleWidth = this._baseProps.width / 5;
    let geo2 = VUtil.createRectangleGeometry(
      this.handleWidth,
      this._baseProps.height,
      this._baseProps.radius
    );
    let mat2 = new MeshBasicMaterial({
      color: this._otherProps.handleColor,
      transparent: true,
      opacity: this._otherProps.handleOpacity,
      map: this._otherProps.handleMap,
      depthTest: this.depthTest,
    });
    VUtil.convertSRGBToLinear(mat2);
    this.handle = new Mesh(geo2, mat2);
    this.handle._vType = "SliderHandle";
    this.handle.renderOrder = this._baseProps.renderOrder + 1;
    this.add(this.handle);

    // 注册光标进入事件
    this.handle.onEcsyHoverEnter = () => {
      this._onHandleHoverEnter();
    };
    // 注册光标退出事件
    this.handle.onEcsyHoverExit = () => {
      this._onHandleHoverExit();
    };
    this.handle.position.x = 0;
    this.handle.position.z = 0.001;

    this.mask = new Mesh(
      new PlaneGeometry(8, 8),
      new MeshBasicMaterial({ transparent: true, opacity: 0.5 })
    );
    this.mask.material.visible = false;

    if (this.vuilib) {
      this.vuilib.addComponents(this.handle, this._priority - 1);
    }
    this.position.z = 0.001;
    this.width = this._baseProps.width;
    this.height = this._baseProps.height;
  }

  clear() {
    super.clear();
    this.mask.geometry.dispose();
    this.mask.material.dispose();
  }

  /**
   * 设置handle位置
   * @param {Number} v 0到1的小数
   */
  setProgressValue(v) {
    v = Math.min(1, Math.max(0, v));

    this.handle.position.x =
      (this._baseProps.width - this.handleWidth) * v -
      this._baseProps.width / 2 +
      this.handleWidth / 2;

    this.value = v;

    for (var i = 0; i < this._otherProps.onValueChange.length; i++) {
      this._otherProps.onValueChange[i](this.value);
    }
  }

  /**
   * 设置进度值
   * @param {Vector3} value 世界坐标
   */
  setValue(value) {
    // 转换到object空间坐标中
    const localPos = this.worldToLocal(value);
    // 限制在width范围内
    let target = localPos.x;
    // 需要去掉handle的宽度
    target = Math.max(
      -this._baseProps.width / 2 + this.handleWidth / 2,
      Math.min(target, this._baseProps.width / 2 - this.handleWidth / 2)
    );
    target = Math.round(target * 1000) / 1000; // 保留3位小数

    if (this.lastTarget === target) return;
    this.lastTarget = target;

    this.handle.position.x = target;

    this.value =
      (target + this._baseProps.width / 2 - this.handleWidth / 2) /
      (this._baseProps.width - this.handleWidth);

    for (var i = 0; i < this._otherProps.onValueChange.length; i++) {
      this._otherProps.onValueChange[i](this.value);
    }
  }

  _onHandleHoverEnter() {
    if (!this.handle || this.isDraging || !this._baseProps.enabled) return;

    this.handle._vrHoverSrcColor = this.handle.material.color.clone();
    this.handle.material.color.offsetHSL(0, 0, 0.2); // 增加 20% 的明度

    for (var i = 0; i < this._otherProps.handleHoverEnter.length; i++) {
      this._otherProps.handleHoverEnter[i](this.handle);
    }
  }

  _onHandleHoverExit() {
    if (!this.handle || this.isDraging || !this._baseProps.enabled) return;

    if (this.handle._vrHoverSrcColor) {
      this.handle.material.color.copy(this.handle._vrHoverSrcColor);
      delete this.handle._vrHoverSrcColor;
    }

    for (var i = 0; i < this._otherProps.handleHoverExit.length; i++) {
      this._otherProps.handleHoverExit[i](this.handle);
    }
  }

  onSelectStart() {
    if (!this._baseProps.enabled) return;
    this.mask.position.z = this.vuilib.getCameraDis();
    this.vuilib.camera.add(this.mask);

    this.isDraging = true;

    for (var i = 0; i < this._otherProps.onValueChangeStart.length; i++) {
      this._otherProps.onValueChangeStart[i](this.value);
    }
  }

  onSelectEnd(obj, intersection) {
    if (!this._baseProps || !this._baseProps.enabled) return;
    // 拖拽滑块结束
    if (this.isDraging) {
      this.mask.removeFromParent();

      this.isDraging = false;

      if (this.handle._vrHoverSrcScale) {
        this.handle.scale.copy(this.handle._vrHoverSrcScale);
        delete this.handle._vrHoverSrcScale;
      }
    }

    // 点击到滑动条，设置滑块到点击位置
    else {
      if (intersection) {
        this.setValue(intersection.point);
      }
    }

    for (var i = 0; i < this._otherProps.onValueChangeEnd.length; i++) {
      this._otherProps.onValueChangeEnd[i](this.value);
    }
  }

  getClickable() {
    if (!this.visible || !this._baseProps.enabled || !this.handle) return [];

    return [this.handle, this];
  }

  /**
   * 增加一个回调函数，当value开始改变调用，参数为当前value值（0-1）
   * @param {*} fun
   */
  addOnValueChangeStartEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChangeStart.push(fun);
  }

  /**
   * 移除一个 当value开始改变调用 的回调函数，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeStartEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChangeStart.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChangeStart.splice(index, 1);
    }
  }

  /**
   * 移除所有 当value开始改变调用 的回调函数
   */
  removeAllOnvalueChangeStartEvent() {
    this._otherProps.onValueChangeStart = [];
  }

  /**
   * 增加一个回调函数，当value改变结束后调用，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  addOnValueChangeEndEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChangeEnd.push(fun);
  }

  /**
   * 移除一个 当value改变结束后调用 的回调函数，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeEndEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChangeEnd.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChangeEnd.splice(index, 1);
    }
  }

  /**
   * 移除所有 当value改变结束后调用 的回调函数
   */
  removeAllOnvalueChangeEndEvent() {
    this._otherProps.onValueChangeEnd = [];
  }

  /**
   * 增加一个回调函数，当value改变过程中调用，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  addOnValueChangeEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChange.push(fun);
  }

  /**
   * 移除一个 当value改变过程中调用 的回调函数，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChange.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChange.splice(index, 1);
    }
  }

  /**
   * 移除所有 当value改变过程中调用 的回调函数
   */
  removeAllOnValueChangeEvent() {
    this._otherProps.onValueChange = [];
  }

  /**
   * 增加一个handle光标进入事件，参数为handle对象
   * @param {*} fun
   * @returns
   */
  addHandlerHoverEnterEvent(fun) {
    if (!fun) return;
    this._otherProps.handleHoverEnter.push(fun);
  }

  /**
   * 移除一个handle光标进入事件，参数为handle对象
   * @param {*} fun
   * @returns
   */
  removeHandlerHoverEnterEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.handleHoverEnter.indexOf(fun);
    if (index >= 0) {
      this._otherProps.handleHoverEnter.splice(index, 1);
    }
  }

  /**
   * 移除所有handle光标进入事件
   */
  removeAllHandlerHoverEnterEvent() {
    this._otherProps.handleHoverEnter = [];
  }

  /**
   * 增加一个handle光标退出事件，参数为handle对象
   * @param {*} fun
   */
  addHandlerHoverExitEvent(fun) {
    if (!fun) return;
    this._otherProps.handleHoverExit.push(fun);
  }

  /**
   * 移除一个handle光标退出事件，参数为handle对象
   * @param {*} fun
   */
  removeHandlerHoverExitEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.handleHoverExit.indexOf(fun);
    if (index >= 0) {
      this._otherProps.handleHoverExit.splice(index, 1);
    }
  }

  /**
   * 移除所有handle光标退出事件
   */
  removeAllHandlerHoverExitEvent() {
    this._otherProps.handleHoverExit = [];
  }
}
